import type { InvoiceRefundStatus } from './invoices';

export interface InvoiceMoneyCents {
  amountPaidCents: number;
  amountRefundedCents: number;
  amountNetPaidCents: number;
  amountRemainingCents: number;
  refundStatus: InvoiceRefundStatus;
}

export function computeInvoiceRefundStatus(
  amountPaidCents: number,
  amountRefundedCents: number
): InvoiceRefundStatus {
  return computeInvoiceMoneyCents({
    amountPaidCents,
    amountRefundedCents,
  }).refundStatus;
}

export function computeInvoiceMoneyCents(invoice: {
  amountCents?: number | null;
  amountPaidCents?: number | null;
  amountRefundedCents?: number | null;
  refundStatus?: InvoiceRefundStatus | string | null;
}): InvoiceMoneyCents {
  const amountCents = Number(invoice.amountCents) || 0;
  const amountPaidCents = Number(invoice.amountPaidCents) || 0;
  const amountRefundedCents = Number(invoice.amountRefundedCents) || 0;
  const amountNetPaidCents = Math.max(0, amountPaidCents - amountRefundedCents);
  let amountRemainingCents = Math.max(0, amountCents - amountNetPaidCents);

  const rawStatus = invoice.refundStatus;
  let refundStatus: InvoiceRefundStatus =
    rawStatus === 'partial' || rawStatus === 'full' ? rawStatus : 'none';
  if (refundStatus === 'none' && amountRefundedCents > 0) {
    refundStatus =
      amountPaidCents > 0 && amountRefundedCents >= amountPaidCents ? 'full' : 'partial';
  }
  if (refundStatus === 'full') {
    amountRemainingCents = 0;
  }

  return {
    amountPaidCents,
    amountRefundedCents,
    amountNetPaidCents,
    amountRemainingCents,
    refundStatus,
  };
}

export function resolveEffectiveInvoiceStatus(
  dbStatus: string | undefined | null,
  money: Pick<InvoiceMoneyCents, 'amountNetPaidCents' | 'amountRemainingCents'>
): string {
  const normalized = String(dbStatus || 'pending').toLowerCase();
  // Sticky terminals: money must not promote void/cancelled to paid.
  if (normalized === 'cancelled') return 'cancelled';
  if (normalized === 'void') return 'void';
  if (money.amountRemainingCents <= 0 && money.amountNetPaidCents > 0) {
    return 'paid';
  }
  if (money.amountNetPaidCents > 0 && money.amountRemainingCents > 0) {
    return 'partially_paid';
  }
  return normalized || 'pending';
}

/** Promo discount cents on an invoice: floored, zero when absent or negative. */
export function computeInvoicePromoOffCents(invoice: {
  promo?: { amountOffCents?: number | null } | null;
}): number {
  return Math.max(0, Math.floor(Number(invoice.promo?.amountOffCents) || 0));
}

/**
 * Cents counted toward settling the gross invoice total: cumulative paid plus the promo
 * discount already reflected in the Stripe charge. The promo only counts once a payment
 * exists, so an unpaid invoice never settles on a discount alone. Returns 0 when unpaid.
 */
export function computeInvoiceSettledCents(invoice: {
  amountPaidCents?: number | null;
  promo?: { amountOffCents?: number | null } | null;
}): number {
  const amountPaidCents = Math.max(0, Number(invoice.amountPaidCents) || 0);
  if (amountPaidCents <= 0) return 0;
  return amountPaidCents + computeInvoicePromoOffCents(invoice);
}

/**
 * Money used for live status. Once a payment exists, the promo discount reduces the
 * remaining balance so a discounted charge that covers the invoice reads as paid.
 * An unpaid promo does not change the remaining balance. A full refund stays at zero.
 */
export function computeInvoiceSettlementMoneyCents(invoice: {
  amountCents?: number | null;
  amountPaidCents?: number | null;
  amountRefundedCents?: number | null;
  refundStatus?: InvoiceRefundStatus | string | null;
  promo?: { amountOffCents?: number | null } | null;
}): InvoiceMoneyCents {
  const money = computeInvoiceMoneyCents(invoice);
  const amountPaidCents = Math.max(0, Number(invoice.amountPaidCents) || 0);
  if (amountPaidCents <= 0 || money.refundStatus === 'full') {
    return money;
  }
  const promoOff = computeInvoicePromoOffCents(invoice);
  if (promoOff <= 0) {
    return money;
  }
  return {
    ...money,
    amountRemainingCents: Math.max(0, money.amountRemainingCents - promoOff),
  };
}
